import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    IconButton,
    Button,
    Typography,
    Tooltip
} from '@mui/material';
import {
    Type,
    PenTool,
    Image as ImageIcon,
    Undo2,
    Redo2,
    Send,
    Search
} from 'lucide-react';
import MathCommandPalette from '../../Components/MathCommandPalette/MathCommandPalette';
import './PremiumMathEditor.css';
import ReferenceTools from "../KaTeX/ReferenceTools";
import ScribblePad from "./ScribblePad";
import PhotoUpload from "./PhotoUpload";
import SymbolLibrary from "./SymbolLibrary";
import { Sigma } from 'lucide-react';

// Import mathlive to register the custom element
import 'mathlive';

interface PremiumMathEditorProps {
    onSubmit?: (content: string) => void;
    activeTab: 'text' | 'draw' | 'upload' | 'tools';
    onTabChange: (tab: string) => void;
}

const MathEditorMain: React.FC<PremiumMathEditorProps> = ({ onSubmit, activeTab, onTabChange }) => {
    const [latex, setLatex] = useState("");
    const [showPalette, setShowPalette] = useState(false);
    const [showSymbolLibrary, setShowSymbolLibrary] = useState(false);
    const mfRef = useRef<any>(null);

    useEffect(() => {
        if (mfRef.current && activeTab === 'text') {
            // Customize mathlive field
            mfRef.current.smartMode = true;
            mfRef.current.virtualKeyboardMode = "manual";
            mfRef.current.placeholder = "Start typing your solution...";

            // Listen for input
            const handleInput = (e: any) => {
                setLatex(e.target.value);
            };
            mfRef.current.addEventListener('input', handleInput);

            // Listen for keydown to intercept Tab
            const handleKeyDown = (e: any) => {
                if (e.key === 'Tab') {
                    const mathfield = mfRef.current;
                    if (mathfield.selectionIsCollapsed && !mathfield.isInsidePlaceholder) {
                        e.preventDefault();
                        setShowPalette(true);
                    }
                }
            };
            mfRef.current.addEventListener('keydown', handleKeyDown);

            return () => {
                if (mfRef.current) {
                    mfRef.current.removeEventListener('input', handleInput);
                    mfRef.current.removeEventListener('keydown', handleKeyDown);
                }
            };
        }
    }, [activeTab]);

    const handlePaletteSelect = (latexSymbol: string, type: 'symbol' | 'template') => {
        if (mfRef.current) {
            mfRef.current.insert(latexSymbol, { focus: true, feedback: true });
        }
        setShowPalette(false);
    };

    const handleSubmit = () => {
        if (onSubmit) {
            onSubmit(latex);
        }
    };

    return (
        <Box className="premium-editor-container">
            {/* Dynamic Tab Toolbar */}
            <Box className="editor-top-toolbar">
                <IconButton
                    className={`toolbar-btn ${activeTab === 'text' ? 'active' : ''}`}
                    onClick={() => onTabChange('text')}
                >
                    <Type size={20} />
                </IconButton>
                <IconButton
                    className={`toolbar-btn ${activeTab === 'draw' ? 'active' : ''}`}
                    onClick={() => onTabChange('draw')}
                >
                    <PenTool size={20} />
                </IconButton>
                <IconButton
                    className={`toolbar-btn ${activeTab === 'upload' ? 'active' : ''}`}
                    onClick={() => onTabChange('upload')}
                >
                    <ImageIcon size={20} />
                </IconButton>
                <Box sx={{ flexGrow: 1 }} />
                <IconButton
                    className={`toolbar-btn ${activeTab === 'tools' ? 'active' : ''}`}
                    onClick={() => onTabChange('tools')}
                >
                    <Search size={20} />
                </IconButton>
                <IconButton
                    className="toolbar-btn"
                    onClick={() => setShowSymbolLibrary(true)}
                >
                    <Sigma size={20} />
                </IconButton>
            </Box>

            {/* Conditional Workspace */}
            <Box className="editor-main-workspace" sx={{ position: 'relative' }}>
                {activeTab === 'text' && (
                    <Box className="math-field-wrapper">
                        <math-field
                            ref={mfRef}
                            className="math-field-control"
                        >
                            {latex}
                        </math-field>
                    </Box>
                )}
                {activeTab === 'draw' && (
                    <ScribblePad
                        onRecognized={(text) => {
                            if (mfRef.current) {
                                // Insert recognized text into mathfield
                                mfRef.current.insert(text, { focus: true, feedback: true });
                            }
                        }}
                    />
                )}
                {activeTab === 'upload' && <PhotoUpload />}
                {activeTab === 'tools' && <ReferenceTools />}

                {/* Palette Overlay */}
                {showPalette && activeTab === 'text' && (
                    <Box className="palette-overlay" onClick={() => setShowPalette(false)}>
                        <Box className="palette-wrapper" onClick={e => e.stopPropagation()}>
                            <MathCommandPalette onSelect={handlePaletteSelect} />
                        </Box>
                    </Box>
                )}

                {/* Symbol Library Modal */}
                <SymbolLibrary
                    open={showSymbolLibrary}
                    onClose={() => setShowSymbolLibrary(false)}
                    onSelect={(latexSymbol) => {
                        if (mfRef.current) {
                            mfRef.current.insert(latexSymbol, { focus: true, feedback: true });
                            // Save to recent
                            const recent = JSON.parse(localStorage.getItem('recent_symbols') || '[]');
                            if (!recent.includes(latexSymbol)) {
                                localStorage.setItem('recent_symbols', JSON.stringify([latexSymbol, ...recent].slice(0, 12)));
                            }
                        }
                    }}
                />
            </Box>

            {/* Standardized Footer */}
            <Box className="editor-footer">
                <Box className="editor-brand">
                    <Box className="bird-logo">
                        <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
                            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" opacity="0.1" />
                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" opacity="0.05" />
                            <path d="M14 12l-4-4v8z" opacity="0.2" />
                            <path d="M10.5 7.5c-1.5 0-3 1-3.5 2.5-.5 1.5.5 3.5 2.5 4-1 .5-2 0-2.5-1-.5-1 .5-2 1.5-2h1c-.5-1.5-2-2.5-3.5-2.5-1.5 0-3 1-3.5 2.5s.5 3.5 2.5 4c-.5.5-1 .5-1.5 0s-.5-1 0-1.5c-1 .5-1.5 1.5-1.5 2.5s1 2 2 2.5 2 .5 3 0c1-.5 1.5-1.5 1.5-2.5s-1-2-2-2.5M19 12l-4-4v8z" />
                        </svg>
                    </Box>
                    <Typography variant="caption" sx={{ ml: 1, color: '#64748b', fontWeight: 600 }}>VietaSpace</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <IconButton size="small" onClick={() => mfRef.current?.executeCommand('undo')}><Undo2 size={18} /></IconButton>
                    <IconButton size="small" onClick={() => mfRef.current?.executeCommand('redo')}><Redo2 size={18} /></IconButton>
                    <Button
                        className="premium-submit-btn"
                        variant="contained"
                        onClick={handleSubmit}
                        endIcon={<Send size={16} />}
                    >
                        Submit Answer
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};

export default MathEditorMain;
